import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { User, Mail, Phone, MapPin, CheckCircle, Save, ArrowLeft, Shield } from "lucide-react";
import userStore from "../zustand/userStore";
import "./ProfilePage.css";

export default function ProfilePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get logged in user data from Redux state or localStorage
  const reduxUser = useSelector((state) => state.signup.user);
  const storedUserRaw = localStorage.getItem("userData");
  const storedUser = reduxUser || (storedUserRaw ? JSON.parse(storedUserRaw) : {});

  // Form State initialized with dynamic user data
  const [formData, setFormData] = useState({
    name: storedUser?.name || storedUser?.email?.split("@")[0] || "",
    email: storedUser?.email || storedUser?.email_id || "",
    contactNumber: storedUser?.contactNumber || storedUser?.mobile_no || "",
    city: storedUser?.city || "",
  });

  const [saving, setSaving] = useState(false);

  // Compute initials dynamically
  const displayName = formData.name || storedUser?.email || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updatedUser = {
        ...storedUser,
        name: formData.name,
        contactNumber: formData.contactNumber,
        mobile_no: formData.contactNumber,
        city: formData.city,
      };

      // Save to localStorage
      localStorage.setItem("userData", JSON.stringify(updatedUser));

      // Save to Zustand userStore if active
      if (userStore.getState().setUser) {
        userStore.getState().setUser(updatedUser);
      }

      toast.success("Profile details updated successfully!");
    } catch (err) {
      console.error("Error saving profile:", err);
      toast.error("Failed to update profile details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-container">
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          border: "none",
          background: "none",
          color: "#2563eb",
          fontWeight: 600,
          fontSize: "14px",
          cursor: "pointer",
          marginBottom: "12px",
          width: "fit-content",
        }}
      >
        <ArrowLeft size={16} /> Back to Dashboard
      </button>

      <div className="profile-card">
        {/* Banner Header */}
        <div className="profile-banner">
          <div className="profile-avatar-large">{initials}</div>
          <div className="profile-banner-info">
            <h2>{displayName}</h2>
            <p>{formData.email || "No email available"}</p>
            <div className="profile-badges">
              <span className="profile-badge profile-badge-active">
                <CheckCircle size={14} /> Account Active
              </span>
              {storedUser?.role?.name && (
                <span className="profile-badge">
                  <Shield size={14} /> {storedUser.role.name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Profile Form Details */}
        <div className="profile-body">
          <form onSubmit={handleSave}>
            <div className="profile-section-title">
              <User size={20} color="#2563eb" /> Personal Information
            </div>

            <div className="profile-grid">
              <div className="profile-field-group">
                <label className="profile-field-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="profile-field-input"
                />
              </div>

              <div className="profile-field-group">
                <label className="profile-field-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled
                  title="Email address cannot be changed"
                  className="profile-field-input"
                />
              </div>

              <div className="profile-field-group">
                <label className="profile-field-label">Contact / Mobile Number</label>
                <input
                  type="text"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  className="profile-field-input"
                />
              </div>

              <div className="profile-field-group">
                <label className="profile-field-label">City / Location</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter city"
                  className="profile-field-input"
                />
              </div>
            </div>

            <div className="profile-actions">
              <button
                type="submit"
                className="profile-btn-save"
                disabled={saving}
              >
                <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
